import { useState, type FormEvent } from "react";
import { contactService } from "../api/services/contactService";
import { useFormSubmit } from "../hooks/useFormSubmit";
import { MAX_MESSAGE_LENGTH, MAX_NAME_LENGTH, validateContactMessage, type ContactMessageFields, type FieldErrors } from "../utils/validation";
import { HoneypotField, TextAreaField, TextField } from "./FormFields";
import { FormSuccess } from "./FormSuccess";
import "./styles/Form.css";

interface ContactFormState extends ContactMessageFields {
  website: string;
}

const EMPTY_FORM: ContactFormState = { name: "", email: "", message: "", website: "" };

export function ContactForm() {
  const [form, setForm] = useState<ContactFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors<keyof ContactMessageFields>>({});
  const { status, errorMessage, submit } = useFormSubmit(contactService.create);

  function handleFieldChange(field: string, value: string) {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found = validateContactMessage(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    void submit({ ...form, name: form.name.trim(), email: form.email.trim(), message: form.message.trim() });
  }

  if (status === "success") {
    return <FormSuccess title="Message sent" message="Thanks for getting in touch. We'll reply to the email address you gave us." />;
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <TextField id="contact-name" name="name" label="Your name" value={form.name} onChange={handleFieldChange} autoComplete="name" maxLength={MAX_NAME_LENGTH} error={errors.name} />
      <TextField id="contact-email" name="email" type="email" label="Email address" value={form.email} onChange={handleFieldChange} autoComplete="email" error={errors.email} />
      <TextAreaField id="contact-message" name="message" label="Message" value={form.message} onChange={handleFieldChange} rows={5} maxLength={MAX_MESSAGE_LENGTH} error={errors.message} />
      <HoneypotField value={form.website} onChange={handleFieldChange} />

      {status === "error" && <p className="form__banner" role="alert">{errorMessage}</p>}

      <button type="submit" className="btn btn--primary btn--lg form__submit" disabled={status === "submitting"}>
        {status === "submitting" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
